import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Modal, Spinner, Alert } from 'react-bootstrap';
import TitleWithBack from '../../components/common/TitleWithBack';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

function CompletedAssignmentsPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const assignment = state?.assignment;

  const [previewFile, setPreviewFile] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [error, setError] = useState(null);

  // Helper to extract file extension
  const getFileExtension = (fileName) => fileName.split('.').pop().toLowerCase();

  // Fetch file blob from the download endpoint and create a new blob with proper MIME type.
  const fetchFileBlob = async (fileId, fileName) => {
    try {
      // Adjust the URL pattern based on your backend endpoint.
      const response = await axios.get(
        `/api/assignments/${assignment.id}/exam-centers/${assignment.examCentreId}/documents/${fileId}/download`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      let contentType = response.headers['content-type'] || 'application/octet-stream';
      // If the file extension is pdf but content-type is not set properly, override it.
      if (getFileExtension(fileName) === 'pdf' && contentType === 'application/octet-stream') {
        contentType = 'application/pdf';
      }
      const blob = new Blob([response.data], { type: contentType });
      console.log("Blob fetched: size=", blob.size, "type=", blob.type);
      return blob;
    } catch (err) {
      console.error("Error fetching file blob:", err);
      setError("Failed to fetch file.");
      return null;
    }
  };

  // Handle preview: fetch the blob, create an object URL, and open modal.
  const handlePreviewFile = async (file) => {
    const blob = await fetchFileBlob(file.id, file.fileName);
    if (blob && blob.size > 0) {
      const objectUrl = URL.createObjectURL(blob);
      setPreviewFile({ ...file, previewUrl: objectUrl });
      setShowPreviewModal(true);
    }
  };

  // Handle download: fetch the blob and trigger a download.
  const handleDownloadFile = async (file) => {
    const blob = await fetchFileBlob(file.id, file.fileName);
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.fileName);
      document.body.appendChild(link);
      link.click();
    }
  };

  // Render inline preview based on file type.
  const renderPreviewContent = (previewUrl, file) => {
    const ext = getFileExtension(file.fileName);
    if (ext === 'pdf') {
      return <embed src={previewUrl} type="application/pdf" width="100%" height="500px" />;
    }
    return <img src={previewUrl} alt={file.fileName} className="img-fluid" />;
  };

  if (!assignment) {
    return (
      <Container className="my-4 text-center">
        <Alert variant="warning">Nothing to show here</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>Go Back</Button>
      </Container>
    );
  }

  return (
    <Container className="mt-2">
      <TitleWithBack title="Completed Assignment Details" />
      <Row className="mb-4">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Header as="h5">Assignment Details</Card.Header>
            <Card.Body>
              <p><strong>Module Name:</strong> {assignment.moduleName}</p>
              <p><strong>Exam Centre:</strong> {assignment.examCentreName}</p>
              <p><strong>Exam Date:</strong> {assignment.examDate}</p>
              <p><strong>Assigned Date:</strong> {new Date(assignment.assignedDate).toLocaleDateString()}</p>
              <p><strong>Status:</strong> {assignment.status}</p>
              <p>
                <strong>Examiner:</strong>{" "}
                {assignment.examiners && assignment.examiners.length
                  ? assignment.examiners[0].fullName
                  : (assignment.examinerName || 'N/A')}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* Uploaded Files Section */}
      {assignment.uploads && assignment.uploads.length > 0 && (
        <>
          <Row className="mb-3">
            <Col>
              <h4>Uploaded Files</h4>
            </Col>
          </Row>
          <Row>
            {assignment.uploads.map((upload) =>
              upload.files.map((file) => (
                <Col key={file.id} xs={12} sm={6} md={4} className="mb-3">
                  <Card className="shadow-sm">
                    <Card.Body>
                      <Card.Title>{upload.documentType}</Card.Title>
                      <Card.Text>{file.fileName}</Card.Text>
                      <div className="d-flex justify-content-around">
                        <Button variant="primary" size="sm" onClick={() => handlePreviewFile(file)}>
                          Preview
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => handleDownloadFile(file)}>
                          Download
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </>
      )}
      <Row className="justify-content-center mt-4">
        <Col xs="auto">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Back to Completed Assignments
          </Button>
        </Col>
      </Row>
      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>File Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewFile && previewFile.previewUrl ? (
            renderPreviewContent(previewFile.previewUrl, previewFile)
          ) : (
            <Spinner animation="border" variant="primary" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
          {previewFile && (
            <Button variant="primary" onClick={() => handleDownloadFile(previewFile)}>
              Download
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
    </Container>
  );
}

export default CompletedAssignmentsPage;
